import { type ReactNode } from "react"
import { Box, Button, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

interface BulkActionsBarProps {
  /** Number of selected items. The bar renders nothing when this is 0. */
  selectedCount: number
  /** Singular noun for the "X foo selected" text. Default "item". */
  entityNoun?: string
  /** Plural override; defaults to `${entityNoun}s`. */
  entityNounPlural?: string
  /** Click handler for the "Clear Selection" button. */
  onClear: () => void
  /** Action buttons rendered on the right. Caller decides which Buttons to show
   *  (e.g. Assign Reseller, Assign Salespeople, Delete N).
   */
  children: ReactNode
}

/**
 * Standard "N items selected" bulk-action toolbar shown above a table.
 * Renders nothing when `selectedCount === 0`, so callers can include it
 * unconditionally.
 *
 * Used across every list page that supports multi-select (customers,
 * prospects, contacts, invoices, email-campaigns, resellers, file-manager).
 */
export function BulkActionsBar({
  selectedCount,
  entityNoun = "item",
  entityNounPlural,
  onClear,
  children,
}: BulkActionsBarProps) {
  if (selectedCount === 0) return null

  const noun = selectedCount > 1 ? entityNounPlural ?? `${entityNoun}s` : entityNoun

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 1,
        border: 1,
        borderColor: "primary.main",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "primary.main" }}>
          {selectedCount} {noun} selected
        </Typography>
        <Button variant="outlined" color="inherit" onClick={onClear} size="small">
          Clear Selection
        </Button>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{children}</Box>
    </Box>
  )
}
